import { useAuth } from "@clerk/expo";
import type { OrganizationCustomRoleKey } from "@clerk/expo/types";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAxiosClient } from "@/hooks/useAxiosClient";
import { useLocalization } from "@/hooks/useLocalizationContext";
import { useRegion } from "@/hooks/useRegion";
import {
  AddAdminOrgMemberRequest,
  AssignAdminPanelsRequest,
  CreateAdminOrgRequest,
  CreateAdminUserRequest,
  OffboardAdminOrgRequest,
  OffboardAdminUserRequest,
  SetAdminCreditsRequest,
  SetAdminOrgOwnerRequest,
  SetAdminPanelOrgPermissionRequest,
  SetAdminUnlimitedCreditsRequest,
  UpdateAdminOrgMemberRequest,
} from "@/types/requests";
import {
  AdminAccountOrg,
  AdminAccountUser,
  ConsoleOrg,
  ConsoleUser,
  ContentHandling,
  PanelAssignMode,
  Region,
  SharePermission,
} from "@/types/resources";
import {
  CreateAdminOrgResponse,
  CreateAdminUserResponse,
  GetAdminAccountsResponse,
} from "@/types/responses";

export interface AssignPanelsInput {
  defaultPanelId: string | null;
  mode: PanelAssignMode;
  orgId: string | null;
  orgPermission: SharePermission | null;
  ownerId: string;
  panelIds: string[];
}

export type NewOrgInput = Pick<
  ConsoleOrg,
  "credits" | "domains" | "imageUrl" | "maxAllowedMemberships" | "name" | "slug"
> & { region?: Region; stripeCustomerId?: string | null };

export type NewUserInput = Pick<
  ConsoleUser,
  "emailAddress" | "firstName" | "lastName"
> & {
  credits: number;
  orgId: string | null;
  orgRole: OrganizationCustomRoleKey | null;
  region?: Region;
  stripeCustomerId?: string | null;
};

interface AccountsValue {
  addMember: (
    orgId: string,
    userId: string,
    role: OrganizationCustomRoleKey
  ) => Promise<void>;
  allUsers: ConsoleUser[];
  assignPanels: (input: AssignPanelsInput) => Promise<void>;
  closeOnboarding: () => void;
  createOrg: (input: NewOrgInput, ownerId: string) => Promise<ConsoleOrg>;
  createUser: (
    input: NewUserInput
  ) => Promise<{ password: string; user: ConsoleUser }>;
  error: string | null;
  hiddenCount: number;
  loading: boolean;
  offboardOrg: (
    orgId: string,
    options: { content: ContentHandling; deactivateMembers: boolean }
  ) => Promise<void>;
  offboardUser: (
    userId: string,
    options: { content: ContentHandling; newOwnerId: string | null }
  ) => Promise<void>;
  onboardingOpen: boolean;
  openOnboarding: () => void;
  orgs: ConsoleOrg[];
  reactivateOrg: (orgId: string) => Promise<void>;
  reactivateUser: (userId: string) => Promise<void>;
  refresh: () => void;
  removeFromOrg: (orgId: string, userId: string) => Promise<void>;
  setCredits: (entityId: string, balance: number) => Promise<void>;
  setOrgOwner: (orgId: string, userId: string) => Promise<void>;
  setPanelOrgPermission: (
    panelId: string,
    orgId: string,
    permission: SharePermission | null
  ) => Promise<void>;
  setRole: (
    orgId: string,
    userId: string,
    role: OrganizationCustomRoleKey
  ) => Promise<void>;
  setUnlimitedCredits: (entityId: string, unlimited: boolean) => Promise<void>;
  users: ConsoleUser[];
}

const AccountsContext = createContext<AccountsValue | null>(null);

const orgRegion = (org: ConsoleOrg): Region =>
  org.publicMetadata.region ?? "us";

const toConsoleOrg = (org: AdminAccountOrg): ConsoleOrg => ({
  createdAt: new Date(org.created_at),
  credits: org.credits,
  domains: org.domains.map((d) => ({
    enrollmentMode: d.enrollment_mode,
    name: d.name,
  })),
  id: org.id,
  imageUrl: org.image_url,
  maxAllowedMemberships: org.max_allowed_memberships,
  name: org.name,
  offboardedAt: org.offboarded_at ? new Date(org.offboarded_at) : null,
  ownerId: org.owner_id,
  panelCount: org.panel_count,
  publicMetadata: org.region ? { region: org.region } : {},
  slug: org.slug,
  sso: org.sso,
  stripeCustomerId: org.stripe_customer_id,
});

const toConsoleUser = (user: AdminAccountUser): ConsoleUser => ({
  banned: user.banned,
  createdAt: new Date(user.created_at),
  credits: user.credits,
  emailAddress: user.email_address,
  firstName: user.first_name,
  id: user.id,
  imageUrl: user.image_url,
  lastName: user.last_name,
  lastSignInAt: user.last_sign_in_at ? new Date(user.last_sign_in_at) : null,
  orgId: user.org_id,
  orgRole: user.org_role,
  ownedPanelCount: user.owned_panel_count,
  panelCount: user.panel_count,
  publicMetadata: user.region ? { region: user.region } : {},
  stripeCustomerId: user.stripe_customer_id,
});

const userRegion = (user: ConsoleUser): Region =>
  user.publicMetadata.region ?? "us";

const AccountsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { isSignedIn } = useAuth();
  const { i18n } = useLocalization();
  const { region } = useRegion();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [orgs, setOrgs] = useState<ConsoleOrg[]>([]);
  const [users, setUsers] = useState<ConsoleUser[]>([]);

  const client = useAxiosClient("/admin");

  const fetchAccounts = useCallback(
    () =>
      client
        .get<GetAdminAccountsResponse>("/accounts")
        .then(({ data }) => {
          setOrgs(data.orgs.map(toConsoleOrg));
          setUsers(data.users.map(toConsoleUser));
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : i18n.t("errors.unknown"));
        })
        .finally(() => {
          setLoading(false);
        }),
    [client, i18n]
  );

  // Re-enter the loading state during render (not in an effect) when the
  // region changes, so the UI shows loading while refetching.
  const [prevRegion, setPrevRegion] = useState(region);
  if (prevRegion !== region) {
    setPrevRegion(region);
    setLoading(true);
    setError(null);
  }

  // Requests are routed to the backend of the region carried by the session
  // token, so switching region must refetch.
  useEffect(() => {
    if (isSignedIn) fetchAccounts();
  }, [fetchAccounts, isSignedIn, region]);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchAccounts();
  }, [fetchAccounts]);

  // Actions triggered without a dialog have nowhere else to show their error.
  const runAction = useCallback(
    (request: Promise<unknown>) =>
      request
        .then(() => fetchAccounts())
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : i18n.t("errors.unknown"));
        }),
    [fetchAccounts, i18n]
  );

  const addMember = useCallback(
    async (orgId: string, userId: string, role: OrganizationCustomRoleKey) => {
      const data: AddAdminOrgMemberRequest = { role, user_id: userId };
      await client.post(`/orgs/${orgId}/members`, data);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const assignPanels = useCallback(
    async ({
      defaultPanelId,
      mode,
      orgId,
      orgPermission,
      ownerId,
      panelIds,
    }: AssignPanelsInput) => {
      const data: AssignAdminPanelsRequest = {
        default_panel_id: defaultPanelId,
        mode,
        org_id: orgId,
        org_permission: orgPermission,
        owner_id: ownerId,
        panel_ids: panelIds,
      };
      await client.post("/panels/assign", data);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const createOrg = useCallback(
    async (
      {
        credits,
        domains,
        imageUrl,
        maxAllowedMemberships,
        name,
        region: target = region,
        slug,
        stripeCustomerId,
      }: NewOrgInput,
      ownerId: string
    ) => {
      const data: CreateAdminOrgRequest = {
        credits,
        domains: domains.map((d) => d.name),
        max_allowed_memberships: maxAllowedMemberships,
        name,
        owner_id: ownerId,
        region: target,
        slug,
        stripe_customer_id: stripeCustomerId,
      };
      const {
        data: { org },
      } = await client.post<CreateAdminOrgResponse>("/orgs", data);
      if (imageUrl) {
        const logo = new FormData();
        logo.append("file", await fetch(imageUrl).then((r) => r.blob()));
        await client.post(`/orgs/${org.id}/logo`, logo);
      }
      await fetchAccounts();
      return toConsoleOrg({ ...org, panel_count: 0 });
    },
    [client, fetchAccounts, region]
  );

  const createUser = useCallback(
    async ({
      credits,
      emailAddress,
      firstName,
      lastName,
      orgId,
      orgRole,
      region: target = region,
      stripeCustomerId,
    }: NewUserInput) => {
      const data: CreateAdminUserRequest = {
        credits,
        email_address: emailAddress,
        first_name: firstName,
        last_name: lastName,
        org_id: orgId,
        org_role: orgRole,
        region: target,
        stripe_customer_id: stripeCustomerId,
      };
      const {
        data: { password, user },
      } = await client.post<CreateAdminUserResponse>("/users", data);
      await fetchAccounts();
      return {
        password,
        user: toConsoleUser({ ...user, owned_panel_count: 0, panel_count: 0 }),
      };
    },
    [client, fetchAccounts, region]
  );

  const offboardOrg = useCallback(
    async (
      orgId: string,
      {
        content,
        deactivateMembers,
      }: { content: ContentHandling; deactivateMembers: boolean }
    ) => {
      const data: OffboardAdminOrgRequest = {
        content,
        deactivate_members: deactivateMembers,
      };
      await client.post(`/orgs/${orgId}/offboard`, data);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const offboardUser = useCallback(
    async (
      userId: string,
      {
        content,
        newOwnerId,
      }: { content: ContentHandling; newOwnerId: string | null }
    ) => {
      const data: OffboardAdminUserRequest = {
        content,
        new_owner_id: newOwnerId,
      };
      await client.post(`/users/${userId}/offboard`, data);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const reactivateOrg = useCallback(
    async (orgId: string) => {
      await client.post(`/orgs/${orgId}/reactivate`);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const reactivateUser = useCallback(
    async (userId: string) => {
      await client.post(`/users/${userId}/reactivate`);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const removeFromOrg = useCallback(
    async (orgId: string, userId: string) => {
      await client.delete(`/orgs/${orgId}/members/${userId}`);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const setCredits = useCallback(
    async (entityId: string, balance: number) => {
      const data: SetAdminCreditsRequest = { balance };
      await client.put(`/credits/${entityId}`, data);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const setOrgOwner = useCallback(
    (orgId: string, userId: string) => {
      const data: SetAdminOrgOwnerRequest = { user_id: userId };
      return runAction(client.put(`/orgs/${orgId}/owner`, data));
    },
    [client, runAction]
  );

  const setPanelOrgPermission = useCallback(
    (panelId: string, orgId: string, permission: SharePermission | null) => {
      const data: SetAdminPanelOrgPermissionRequest = { permission };
      return runAction(client.put(`/panels/${panelId}/orgs/${orgId}`, data));
    },
    [client, runAction]
  );

  const setRole = useCallback(
    (orgId: string, userId: string, role: OrganizationCustomRoleKey) => {
      const data: UpdateAdminOrgMemberRequest = { role };
      return runAction(client.patch(`/orgs/${orgId}/members/${userId}`, data));
    },
    [client, runAction]
  );

  const setUnlimitedCredits = useCallback(
    async (entityId: string, unlimited: boolean) => {
      const data: SetAdminUnlimitedCreditsRequest = { unlimited };
      await client.put(`/credits/${entityId}/unlimited`, data);
      await fetchAccounts();
    },
    [client, fetchAccounts]
  );

  const value = useMemo<AccountsValue>(
    () => ({
      addMember,
      allUsers: users,
      assignPanels,
      closeOnboarding: () => setOnboardingOpen(false),
      createOrg,
      createUser,
      error,
      hiddenCount:
        orgs.filter((o) => orgRegion(o) !== region).length +
        users.filter((u) => !u.orgId && userRegion(u) !== region).length,
      loading,
      offboardOrg,
      offboardUser,
      onboardingOpen,
      openOnboarding: () => setOnboardingOpen(true),
      orgs: orgs.filter((o) => orgRegion(o) === region),
      reactivateOrg,
      reactivateUser,
      refresh,
      removeFromOrg,
      setCredits,
      setOrgOwner,
      setPanelOrgPermission,
      setRole,
      setUnlimitedCredits,
      users: users.filter((u) => userRegion(u) === region),
    }),
    [
      addMember,
      assignPanels,
      createOrg,
      createUser,
      error,
      loading,
      offboardOrg,
      offboardUser,
      onboardingOpen,
      orgs,
      reactivateOrg,
      reactivateUser,
      refresh,
      region,
      removeFromOrg,
      setCredits,
      setOrgOwner,
      setPanelOrgPermission,
      setRole,
      setUnlimitedCredits,
      users,
    ]
  );

  return (
    <AccountsContext.Provider value={value}>
      {children}
    </AccountsContext.Provider>
  );
};

const useAccounts = (): AccountsValue => {
  const context = useContext(AccountsContext);
  if (!context) {
    throw new Error("useAccounts must be used in AccountsProvider");
  }
  return context;
};

export { AccountsProvider, orgRegion, useAccounts, userRegion };
